/*
 * The Oud Co — storefront behaviour.
 * One ES module, no dependencies. Every component is a custom element so it
 * initialises itself, including after the theme editor re-renders a section.
 */

const theme = window.theme || { routes: {}, strings: {} };
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const desktopQuery = window.matchMedia('(min-width: 1008px)');

/* ------------------------------------------------------------------ helpers */

const debounce = (fn, wait = 300) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
};

const define = (name, ctor) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
};

const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';

const focusables = (root) =>
  Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === document.activeElement);

const storage = {
  get(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  set(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* storage unavailable (private mode) — nothing to do */
    }
  },
};

/* Shared page overlay, used by drawers and the header search. */
const overlay = {
  owners: new Set(),
  get el() {
    return document.querySelector('[data-page-overlay]');
  },
  /* belowHeader: dim the page but leave the header (and its search bar) on top. */
  show(owner, { belowHeader = false } = {}) {
    this.owners.add(owner);
    this.el?.classList.add('is-visible');
    this.el?.classList.toggle('page-overlay--below-header', belowHeader);
  },
  hide(owner) {
    this.owners.delete(owner);
    if (this.owners.size === 0) this.el?.classList.remove('is-visible', 'page-overlay--below-header');
  },
};

document.addEventListener('click', (event) => {
  if (event.target.closest('[data-page-overlay]')) {
    document.querySelectorAll('drawer-panel.is-open, cart-drawer.is-open').forEach((d) => d.close());
    document.querySelector('predictive-search.is-open')?.close();
  }
});

/* ------------------------------------------------------------------ drawers */

class DrawerPanel extends HTMLElement {
  connectedCallback() {
    // Drawers rendered inside the sticky header would sit under the page
    // overlay (the header is its own stacking context), so move them to <body>.
    if (this.hasAttribute('data-portal') && this.parentElement !== document.body) {
      document.querySelectorAll(`body > #${this.id}`).forEach((old) => old !== this && old.remove());
      document.body.append(this);
      return;
    }
    if (this.initialised) return;
    this.initialised = true;
    this.onKeydown = this.onKeydown.bind(this);
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]')) this.close();
    });
  }

  open(trigger) {
    if (this.classList.contains('is-open')) return;
    this.trigger = trigger || document.activeElement;
    // Only one drawer at a time.
    document.querySelectorAll('drawer-panel.is-open, cart-drawer.is-open').forEach((d) => d !== this && d.close(false));
    this.hidden = false;
    requestAnimationFrame(() => this.classList.add('is-open'));
    overlay.show(this);
    document.documentElement.classList.add('is-locked');
    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    document.addEventListener('keydown', this.onKeydown);
    setTimeout(() => (focusables(this)[0] || this).focus({ preventScroll: true }), 80);
  }

  close(restoreFocus = true) {
    if (!this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    overlay.hide(this);
    if (overlay.owners.size === 0) document.documentElement.classList.remove('is-locked');
    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    document.removeEventListener('keydown', this.onKeydown);
    if (restoreFocus && this.trigger && document.contains(this.trigger)) this.trigger.focus({ preventScroll: true });
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = focusables(this);
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}
define('drawer-panel', DrawerPanel);

/* Any element with data-drawer-open="Id" opens that drawer. */
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-drawer-open]');
  if (!opener) return;
  const drawer = document.getElementById(opener.dataset.drawerOpen);
  if (!drawer || typeof drawer.open !== 'function') return;
  event.preventDefault();
  drawer.open(opener);
});

/* Plain toggles (mobile menu accordions). */
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-collapsible-toggle]');
  if (!toggle) return;
  const target = document.getElementById(toggle.getAttribute('aria-controls'));
  if (!target) return;
  const expanded = toggle.getAttribute('aria-expanded') === 'true';
  toggle.setAttribute('aria-expanded', String(!expanded));
  target.hidden = expanded;
});

/* ------------------------------------------------------------------ disclosure menu (currency, sort) */

class DisclosureMenu extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector(':scope > button');
    this.list = this.querySelector(':scope > ul');
    if (!this.button || !this.list) return;
    this.button.addEventListener('click', () => this.toggle());
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        this.toggle(false);
        this.button.focus();
      }
    });
    this.onOutside = (event) => {
      if (!this.contains(event.target)) this.toggle(false);
    };
  }

  get isOpen() {
    return this.button.getAttribute('aria-expanded') === 'true';
  }

  toggle(force) {
    const open = force ?? !this.isOpen;
    this.button.setAttribute('aria-expanded', String(open));
    this.list.hidden = !open;
    if (open) document.addEventListener('click', this.onOutside);
    else document.removeEventListener('click', this.onOutside);
  }
}
define('disclosure-menu', DisclosureMenu);

/* ------------------------------------------------------------------ header */

class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('.nav-list > [data-dropdown-item]'));
    this.querySelectorAll('[data-dropdown-item]').forEach((item) => this.setupDropdown(item));

    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.closeAll();
    });

    this.querySelector('[data-search-toggle]')?.addEventListener('click', () => {
      document.getElementById('HeaderSearch')?.toggle();
    });

    // Keep --header-height accurate for sticky offsets.
    const setHeight = () => document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
    new ResizeObserver(setHeight).observe(this);
  }

  setupDropdown(item) {
    const button = item.querySelector(':scope > button[aria-expanded]');
    if (!button) return;
    let closeTimer;

    const open = () => {
      clearTimeout(closeTimer);
      // Close siblings at the same level.
      Array.from(item.parentElement.children).forEach((sibling) => {
        if (sibling !== item) this.setOpen(sibling, false);
      });
      this.setOpen(item, true);
    };
    const close = () => {
      closeTimer = setTimeout(() => this.setOpen(item, false), 150);
    };

    item.addEventListener('mouseenter', () => desktopQuery.matches && open());
    item.addEventListener('mouseleave', () => desktopQuery.matches && close());
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      const isOpen = button.getAttribute('aria-expanded') === 'true';
      if (isOpen) this.setOpen(item, false);
      else open();
    });
    item.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.setOpen(item, false);
        button.focus();
        event.stopPropagation();
      }
    });
    item.addEventListener('focusout', (event) => {
      if (!item.contains(event.relatedTarget)) this.setOpen(item, false);
    });
  }

  setOpen(item, open) {
    const button = item.querySelector(':scope > button[aria-expanded]');
    if (!button) return;
    button.setAttribute('aria-expanded', String(open));
    item.classList.toggle('is-open', open);
    if (!open) item.querySelectorAll('[data-dropdown-item]').forEach((child) => this.setOpen(child, false));
  }

  closeAll() {
    this.items.forEach((item) => this.setOpen(item, false));
  }
}
define('site-header', SiteHeader);

/* ------------------------------------------------------------------ predictive search */

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('.header-search__results');
    this.toggleButton = document.querySelector('[data-search-toggle]');
    this.onKeydown = (event) => {
      if (event.key === 'Escape') this.close();
    };
    this.querySelector('[data-search-close]')?.addEventListener('click', () => this.close());
    this.input?.addEventListener('input', debounce(() => this.search(), 300));
  }

  toggle() {
    if (this.classList.contains('is-open')) this.close();
    else this.open();
  }

  open() {
    this.classList.add('is-open');
    this.toggleButton?.setAttribute('aria-expanded', 'true');
    overlay.show(this, { belowHeader: true });
    document.addEventListener('keydown', this.onKeydown);
    setTimeout(() => this.input?.focus(), 50);
  }

  close() {
    this.classList.remove('is-open');
    this.toggleButton?.setAttribute('aria-expanded', 'false');
    overlay.hide(this);
    document.removeEventListener('keydown', this.onKeydown);
    this.toggleButton?.focus({ preventScroll: true });
  }

  async search() {
    const term = this.input.value.trim();
    this.controller?.abort();
    if (term.length === 0) {
      this.results.innerHTML = '';
      this.input.setAttribute('aria-expanded', 'false');
      return;
    }
    this.controller = new AbortController();
    const type = this.querySelector('input[name="type"]')?.value || 'product';
    const params = new URLSearchParams({
      q: term,
      'resources[type]': type,
      'resources[limit]': '4',
      'resources[options][prefix]': 'last',
      section_id: 'predictive-search',
    });
    try {
      const response = await fetch(`${theme.routes.predictiveSearch}?${params}`, { signal: this.controller.signal });
      if (!response.ok) throw new Error(response.status);
      const html = await response.text();
      const content = parseHTML(html).querySelector('#HeaderSearchResults-Content');
      this.results.innerHTML = content ? content.outerHTML : '';
      this.input.setAttribute('aria-expanded', content ? 'true' : 'false');
    } catch (error) {
      if (error.name !== 'AbortError') this.results.innerHTML = '';
    }
  }
}
define('predictive-search', PredictiveSearch);

/* ------------------------------------------------------------------ hero slideshow */

class HeroSlideshow extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.hero__slide'));
    this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
    this.index = 0;

    this.slides.forEach((slide) => this.whenLoaded(slide));

    if (this.slides.length < 2) return;
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.heroDot), true)));
    this.querySelector('[data-hero-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
    this.querySelector('[data-hero-next]')?.addEventListener('click', () => this.go(this.index + 1, true));

    this.autoplay = this.dataset.autoplay === 'true' && !reducedMotion.matches;
    this.delay = Number(this.dataset.speed || 5) * 1000;
    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());
    this.start();
  }

  disconnectedCallback() {
    this.stop();
  }

  whenLoaded(slide) {
    const img = slide.querySelector('img');
    const ready = () => slide.classList.add('is-ready');
    if (!img || img.complete) ready();
    else {
      img.addEventListener('load', ready, { once: true });
      img.addEventListener('error', ready, { once: true });
    }
  }

  go(index, manual = false) {
    const count = this.slides.length;
    this.index = (index + count) % count;
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('aria-hidden', !active);
      slide.querySelectorAll('a, button').forEach((el) => (el.tabIndex = active ? 0 : -1));
    });
    this.dots.forEach((dot, i) => dot.toggleAttribute('aria-current', i === this.index));
    if (manual) this.restart();
  }

  start() {
    if (!this.autoplay) return;
    this.stop();
    this.timer = setInterval(() => this.go(this.index + 1), this.delay);
  }

  stop() {
    clearInterval(this.timer);
  }

  restart() {
    this.stop();
    this.start();
  }
}
define('hero-slideshow', HeroSlideshow);

/* ------------------------------------------------------------------ newsletter popup */

class NewsletterPopup extends HTMLElement {
  connectedCallback() {
    this.key = 'oudco:newsletter-popup-seen';
    this.querySelector('[data-popup-close]')?.addEventListener('click', () => this.close());
    this.addEventListener('keydown', (event) => event.key === 'Escape' && this.close());

    // After a sign-up Shopify reloads the page; show the confirmation.
    const posted = this.querySelector('.form-message') && /customer_posted=true|contact_posted=true/.test(window.location.search + window.location.hash);
    if (posted) {
      this.show();
      return;
    }

    if (this.dataset.visitorsOnly === 'true' && this.dataset.loggedIn === 'true') return;
    if (this.dataset.once === 'true' && storage.get(this.key)) return;
    if (window.Shopify && window.Shopify.designMode) return;

    setTimeout(() => this.show(), Number(this.dataset.delay || 5) * 1000);
  }

  show() {
    this.hidden = false;
    requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-visible')));
    storage.set(this.key, '1');
  }

  close() {
    this.classList.remove('is-visible');
    setTimeout(() => (this.hidden = true), 500);
  }
}
define('newsletter-popup', NewsletterPopup);

/* ------------------------------------------------------------------ reveal on scroll */

const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -10% 0px' }
      )
    : null;

const observeReveals = (root = document) => {
  root.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((el) => {
    if (revealObserver && !reducedMotion.matches) revealObserver.observe(el);
    else el.classList.add('is-revealed');
  });
};

/* ------------------------------------------------------------------ quantity input */

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-quantity]');
      if (!button) return;
      const min = Number(this.input.min || 0);
      const current = Number(this.input.value) || 0;
      const next = button.dataset.quantity === 'plus' ? current + 1 : Math.max(min, current - 1);
      if (next === current) return;
      this.input.value = next;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
}
define('quantity-input', QuantityInput);

/* ------------------------------------------------------------------ cart */

const cartSectionIds = () => {
  const ids = [];
  if (document.getElementById('CartDrawer')) ids.push('cart-drawer');
  const page = document.querySelector('cart-page');
  if (page) ids.push(page.dataset.sectionId);
  const header = document.querySelector('site-header')?.closest('.shopify-section');
  if (header) ids.push(header.id.replace('shopify-section-', ''));
  return ids;
};

/* Replace the cart regions and the header cart dot from a sections payload. */
const renderCartSections = (sections) => {
  if (!sections) return;
  Object.entries(sections).forEach(([id, html]) => {
    if (!html) return;
    const doc = parseHTML(html);
    if (id === 'cart-drawer') {
      const fresh = doc.querySelector('#CartDrawer [data-cart-contents]');
      const current = document.querySelector('#CartDrawer [data-cart-contents]');
      if (fresh && current) current.replaceWith(fresh);
    } else if (doc.querySelector('cart-page')) {
      const fresh = doc.querySelector('cart-page [data-cart-contents]');
      const current = document.querySelector('cart-page [data-cart-contents]');
      if (fresh && current) current.replaceWith(fresh);
    } else {
      const fresh = doc.getElementById('CartCount');
      const current = document.getElementById('CartCount');
      if (fresh && current) current.replaceWith(fresh);
    }
  });
};

const cartRequest = async (url, body) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: body instanceof FormData ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: body instanceof FormData ? body : JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status) {
    const message = data.description || data.message || theme.strings.cartError;
    throw new Error(typeof message === 'string' ? message : theme.strings.cartError);
  }
  return data;
};

/* Shared behaviour for the drawer and the cart page: quantities, remove, note. */
const CartRegion = (Base) =>
  class extends Base {
    connectedCallback() {
      super.connectedCallback?.();
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-cart-quantity] input')) this.queueChange(event.target);
      });
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-cart-remove]');
        if (!remove) return;
        event.preventDefault();
        this.change(Number(remove.dataset.cartRemove), 0);
      });
      this.addEventListener(
        'input',
        debounce((event) => {
          if (event.target.matches('[data-cart-note]')) {
            cartRequest(theme.routes.cartUpdate + '.js', { note: event.target.value }).catch(() => {});
          }
        }, 500)
      );
      this.pending = new Map();
      this.flush = debounce(() => this.flushChanges(), 350);
    }

    queueChange(input) {
      this.pending.set(Number(input.dataset.line), Math.max(0, Number(input.value) || 0));
      this.flush();
    }

    async flushChanges() {
      const changes = Array.from(this.pending.entries());
      this.pending.clear();
      for (const [line, quantity] of changes) {
        // eslint-disable-next-line no-await-in-loop
        await this.change(line, quantity);
      }
    }

    async change(line, quantity) {
      const region = this.querySelector('[data-cart-contents]');
      region?.setAttribute('aria-busy', 'true');
      try {
        const data = await cartRequest(theme.routes.cartChange + '.js', {
          line,
          quantity,
          sections: cartSectionIds(),
          sections_url: window.location.pathname,
        });
        renderCartSections(data.sections);
        // The server may cap the quantity at available stock.
        const item = data.items?.[line - 1];
        if (quantity > 0 && item && item.quantity < quantity) this.showError(theme.strings.cartError);
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.querySelector('[data-cart-contents]')?.removeAttribute('aria-busy');
      }
    }

    showError(message) {
      const box = this.querySelector('[data-cart-error]');
      if (!box) return;
      box.textContent = message;
      box.hidden = false;
    }
  };

class CartDrawer extends CartRegion(DrawerPanel) {}
define('cart-drawer', CartDrawer);

class CartPage extends CartRegion(HTMLElement) {}
define('cart-page', CartPage);

/* ------------------------------------------------------------------ fragrance selector */

class FragranceSelector extends HTMLElement {
  connectedCallback() {
    this.selects = Array.from(this.querySelectorAll('select'));
    this.selects.forEach((select) =>
      select.addEventListener('change', () => {
        if (select.value) this.setError(select, false);
      })
    );
  }

  /* Returns the first invalid select, or null when every choice is made. */
  validate() {
    let firstInvalid = null;
    this.selects.forEach((select) => {
      const invalid = !select.value;
      this.setError(select, invalid);
      if (invalid && !firstInvalid) firstInvalid = select;
    });
    return firstInvalid;
  }

  setError(select, invalid) {
    select.setAttribute('aria-invalid', String(invalid));
    const error = document.getElementById(`${select.id}-Error`);
    if (error) error.hidden = !invalid;
  }
}
define('fragrance-selector', FragranceSelector);

/* ------------------------------------------------------------------ product page */

class ProductPage extends HTMLElement {
  connectedCallback() {
    this.form = document.getElementById(this.dataset.formId);
    if (!this.form) return;
    this.submitButton = this.form.querySelector('[data-add-to-cart]');
    this.errorBox = this.form.querySelector('[data-form-error]');
    this.form.addEventListener('submit', (event) => this.onSubmit(event));

    // The info column is sticky beside the gallery. When it is taller than the
    // screen (e.g. with the fragrance choices), stick it by its bottom edge so
    // every field and the add-to-cart button can still be reached.
    this.info = this.querySelector('.product__info');
    if (this.info && 'ResizeObserver' in window) {
      const update = () => {
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 96;
        const preferred = header + 50;
        const fitTop = window.innerHeight - this.info.offsetHeight - 30;
        this.info.style.setProperty('--info-top', `${Math.min(preferred, fitTop)}px`);
      };
      new ResizeObserver(update).observe(this.info);
      window.addEventListener('resize', debounce(update, 100));
      update();
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.submitButton.disabled || this.submitButton.getAttribute('aria-busy') === 'true') return;
    this.hideError();

    // Every fragrance choice is required before adding to cart.
    const fragrance = this.querySelector('fragrance-selector');
    const invalid = fragrance?.validate();
    if (invalid) {
      invalid.focus();
      this.showError(theme.strings.fragranceRequired);
      return;
    }

    if (theme.cartType === 'page' || !document.getElementById('CartDrawer')) {
      this.form.submit();
      return;
    }

    this.submitButton.setAttribute('aria-busy', 'true');
    const body = new FormData(this.form);
    // Selects linked with form="…" are included by FormData automatically.
    body.append('sections', cartSectionIds().join(','));
    body.append('sections_url', window.location.pathname);

    try {
      const data = await cartRequest(theme.routes.cartAdd + '.js', body);
      renderCartSections(data.sections);
      document.getElementById('CartDrawer')?.open(this.submitButton);
      document.dispatchEvent(new CustomEvent('product:added', { detail: { item: data } }));
    } catch (error) {
      this.showError(error.message);
    } finally {
      this.submitButton.removeAttribute('aria-busy');
    }
  }

  showError(message) {
    if (!this.errorBox) return;
    this.errorBox.textContent = message;
    this.errorBox.hidden = false;
  }

  hideError() {
    if (this.errorBox) this.errorBox.hidden = true;
  }
}
define('product-page', ProductPage);

class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variants-json]')?.textContent || '[]');
    this.selects = Array.from(this.querySelectorAll('select'));
    this.page = this.closest('product-page');
    this.form = document.getElementById(this.dataset.formId);
    this.addEventListener('change', () => this.onChange());
  }

  onChange() {
    const options = this.selects.map((select) => select.value);
    this.selects.forEach((select) => {
      const label = select.closest('.variant-select')?.querySelector('[data-option-value]');
      if (label) label.textContent = select.value;
    });
    const variant = this.variants.find((v) => v.options.every((value, i) => value === options[i]));
    this.update(variant);
  }

  update(variant) {
    const button = this.form?.querySelector('[data-add-to-cart]');
    const buttonText = button?.querySelector('[data-add-to-cart-text]');
    const lowStock = this.form?.querySelector('[data-low-stock]');
    const isPreOrder = this.page?.dataset.preOrder === 'true';

    if (!variant) {
      if (button) button.disabled = true;
      if (buttonText) buttonText.textContent = theme.strings.unavailable;
      if (lowStock) lowStock.textContent = '';
      return;
    }

    const idInput = this.form?.querySelector('[data-variant-id]');
    if (idInput) {
      idInput.value = variant.id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (button) button.disabled = !variant.available;
    if (buttonText) {
      buttonText.textContent = variant.available ? (isPreOrder ? theme.strings.preOrder : theme.strings.addToCart) : theme.strings.soldOut;
    }

    if (lowStock) {
      // lowStockOther is rendered for a count of 999; swap in the real number.
      lowStock.textContent =
        variant.low_stock && variant.low_stock > 0
          ? variant.low_stock === 1
            ? theme.strings.lowStockOne
            : theme.strings.lowStockOther.replace('999', variant.low_stock)
          : '';
    }

    const price = this.page?.querySelector('.product__price .price');
    if (price) {
      price.classList.toggle('price--sale', Boolean(variant.compare_at_price));
      const current = price.querySelector('.price__current');
      if (current) current.textContent = variant.price;
      let compare = price.querySelector('.price__compare');
      if (variant.compare_at_price) {
        if (!compare) {
          compare = document.createElement('s');
          compare.className = 'price__compare';
          price.append(compare);
        }
        compare.textContent = variant.compare_at_price;
      } else {
        compare?.remove();
      }
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    if (variant.featured_media_id) this.page?.querySelector('product-gallery')?.showMedia(variant.featured_media_id);
    this.page?.querySelector('pickup-availability')?.load(variant.id, variant.available);
  }
}
define('variant-picker', VariantPicker);

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.items = Array.from(this.querySelectorAll('.product-gallery__item'));
    this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
    this.dialog = this.querySelector('[data-zoom-dialog]');
    this.current = 0;

    this.track?.addEventListener(
      'scroll',
      debounce(() => {
        if (desktopQuery.matches) return;
        const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
        this.setCurrent(index);
      }, 60)
    );
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.galleryDot))));
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.goTo(this.current - 1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.goTo(this.current + 1));

    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-zoom-index]');
      if (trigger && !trigger.disabled) {
        const index = trigger.dataset.zoomIndex === 'current' ? this.current : Number(trigger.dataset.zoomIndex);
        this.openZoom(index, trigger);
      }
      if (event.target.closest('[data-share-button]')) this.share();
    });

    if (this.dialog) {
      this.dialog.addEventListener('click', (event) => {
        if (event.target.closest('[data-zoom-close]') || event.target.tagName === 'IMG') this.dialog.close();
      });
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('is-locked');
        this.zoomTrigger?.focus({ preventScroll: true });
      });
    }
  }

  setCurrent(index) {
    this.current = Math.max(0, Math.min(index, this.items.length - 1));
    this.dots.forEach((dot, i) => dot.toggleAttribute('aria-current', i === this.current));
  }

  goTo(index) {
    const count = this.items.length;
    const next = (index + count) % count;
    this.setCurrent(next);
    const item = this.items[next];
    if (!item) return;
    if (desktopQuery.matches) {
      item.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
    } else {
      this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }
  }

  showMedia(mediaId) {
    const index = this.items.findIndex((item) => item.dataset.mediaId === String(mediaId));
    if (index < 0) return;
    if (desktopQuery.matches) {
      // On desktop the images are stacked; only scroll if the image is off screen.
      const rect = this.items[index].getBoundingClientRect();
      if (rect.top < 0 || rect.top > window.innerHeight * 0.6) this.goTo(index);
      else this.setCurrent(index);
    } else {
      this.goTo(index);
    }
  }

  openZoom(index, trigger) {
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
    this.zoomTrigger = trigger;
    this.dialog.showModal();
    document.documentElement.classList.add('is-locked');
    const image = this.dialog.querySelector(`[data-zoom-image="${index}"]`);
    image?.scrollIntoView({ block: 'start' });
  }

  async share() {
    const data = { title: document.title, url: window.location.href };
    if (navigator.share) {
      try {
        await navigator.share(data);
      } catch (e) {
        /* share sheet dismissed */
      }
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(data.url).catch(() => {});
    }
  }
}
define('product-gallery', ProductGallery);

class PickupAvailability extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('data-available')) this.load(this.dataset.variantId, true);
  }

  async load(variantId, available) {
    if (!available) {
      this.innerHTML = '';
      return;
    }
    const root = (this.dataset.rootUrl || '/').replace(/\/$/, '');
    try {
      const response = await fetch(`${root}/variants/${variantId}/?section_id=pickup-availability`);
      if (!response.ok) throw new Error(response.status);
      const doc = parseHTML(await response.text());
      const section = doc.querySelector('.shopify-section');
      this.innerHTML = section ? section.innerHTML : '';
      // The store-list drawer lives in this element; move it to <body> so it overlays correctly.
      const drawer = this.querySelector('#PickupDrawer');
      if (drawer) {
        document.getElementById('PickupDrawer')?.remove();
        document.body.append(drawer);
      }
    } catch (e) {
      this.innerHTML = '';
    }
  }
}
define('pickup-availability', PickupAvailability);

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.children.length > 0 || !this.dataset.url) return;
    const load = async () => {
      try {
        const response = await fetch(this.dataset.url);
        const doc = parseHTML(await response.text());
        const fresh = doc.querySelector('product-recommendations');
        if (fresh && fresh.innerHTML.trim()) {
          this.innerHTML = fresh.innerHTML;
          observeReveals(this);
        }
      } catch (e) {
        /* recommendations are optional */
      }
    };
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            observer.disconnect();
            load();
          }
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      observer.observe(this);
    } else {
      load();
    }
  }
}
define('product-recommendations', ProductRecommendations);

/* ------------------------------------------------------------------ collection: sort + filters */

class CollectionPage extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.addEventListener('click', (event) => {
      const option = event.target.closest('[data-sort-value]');
      if (!option) return;
      const params = new URLSearchParams(window.location.search);
      params.set('sort_by', option.dataset.sortValue);
      params.delete('page');
      this.render(params);
    });
    this.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-filter-form]');
      if (!form) return;
      event.preventDefault();
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (String(value).trim() !== '') params.append(key, value);
      });
      this.render(params);
    });
    this.addEventListener('click', (event) => {
      const reset = event.target.closest('[data-filter-reset]');
      if (!reset) return;
      event.preventDefault();
      this.render(new URL(reset.href, window.location.origin).searchParams);
    });
    window.addEventListener('popstate', () => this.render(new URLSearchParams(window.location.search), false));
  }

  async render(params, push = true) {
    document.getElementById('FilterDrawer')?.close(false);
    const products = this.querySelector('.collection__products');
    products?.setAttribute('aria-busy', 'true');
    const query = params.toString();
    try {
      const response = await fetch(`${window.location.pathname}?${query}${query ? '&' : ''}section_id=${this.sectionId}`);
      if (!response.ok) throw new Error(response.status);
      const fresh = parseHTML(await response.text()).querySelector('collection-page');
      if (fresh) {
        this.innerHTML = fresh.innerHTML;
        observeReveals(this);
      }
      if (push) window.history.pushState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
    } catch (e) {
      window.location.search = query;
    } finally {
      this.querySelector('.collection__products')?.removeAttribute('aria-busy');
    }
  }
}
define('collection-page', CollectionPage);

/* Forms with data-confirm on their submit button ask first (e.g. delete address). */
document.addEventListener('submit', (event) => {
  const button = event.submitter?.closest('[data-confirm]') || event.target.querySelector('[data-confirm]');
  if (button && !window.confirm(button.dataset.confirm)) event.preventDefault();
});

/* ------------------------------------------------------------------ init */

observeReveals();

// Theme editor: re-run reveal and close overlays when sections reload.
document.addEventListener('shopify:section:load', (event) => observeReveals(event.target));
document.addEventListener('shopify:section:unload', () => overlay.owners.clear());
